/**
 * Matcher AST Nodes
 *
 * Hierarchy:
 *   MatcherBlock
 *     └─ MatcherRow[]
 *          └─ MatcherItem[]        ← display-only, no inputs allowed
 *               └─ DisplayInlineNode[]
 *     └─ answers: string[][]       ← unordered groups of itemIds
 *
 * Invariants enforced by the schema:
 *   1. itemIds are globally unique across all rows
 *   2. Every itemId referenced in `answers` is declared in a row
 *   3. No itemId appears in more than one answer group
 *   4. Every answer group contains at least 1 itemId
 */

import { z } from 'zod';
import {
  MatcherItemSchema,
  MatcherItem,
} from './matcher-item';
import { AST_TYPES } from '../constants';

// Matcher Row

export const MatcherRowSchema = z.object({
  type: z.literal(AST_TYPES.MATCHER_ROW),

  /** At least one item per row */
  items: z.array(MatcherItemSchema).min(1),
});

export type MatcherRow = z.infer<typeof MatcherRowSchema>;

export function createMatcherRow(items: MatcherItem[]): MatcherRow {
  return MatcherRowSchema.parse({ type: AST_TYPES.MATCHER_ROW, items });
}

export function isMatcherRow(value: unknown): value is MatcherRow {
  return MatcherRowSchema.safeParse(value).success;
}

// Matcher Block

export const MatcherBlockSchema = z
  .object({
    type: z.literal(AST_TYPES.MATCHER_BLOCK),
    name: z.string().min(1),

    /** Optional palette of colors for the renderer (one per answer group) */
    colors: z.array(z.string()).optional(),

    /** At least one row required */
    rows: z.array(MatcherRowSchema).min(1),
    answers: z.array(z.array(z.string().min(1)).min(1)).min(1),
  })
  .superRefine((data, ctx) => {
    const declaredIds = collectDeclaredItemIds(data, ctx);
    validateAnswerReferences(data, declaredIds, ctx);
    validateAnswerUniqueness(data, ctx);
  });

export type MatcherBlock = z.infer<typeof MatcherBlockSchema>;

// superRefine helpers

/**
 * Invariant 1 — itemIds are globally unique across all rows.
 *
 * Returns the set of all valid declared itemIds so subsequent validators can
 * re-use it without rescanning the rows.
 */
function collectDeclaredItemIds(
  data: z.infer<typeof MatcherBlockSchema>,
  ctx: z.RefinementCtx,
): Set<string> {
  const declared = new Set<string>();

  data.rows.forEach((row, rowIndex) => {
    row.items.forEach((item, itemIndex) => {
      if (declared.has(item.itemId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate itemId "${item.itemId}" — each item must have a unique id across all rows`,
          path: ['rows', rowIndex, 'items', itemIndex, 'itemId'],
        });
      } else {
        declared.add(item.itemId);
      }
    });
  });

  return declared;
}

/**
 * Invariant 2 — every itemId referenced in `answers` is declared in a row.
 */
function validateAnswerReferences(
  data: z.infer<typeof MatcherBlockSchema>,
  declaredIds: Set<string>,
  ctx: z.RefinementCtx,
): void {
  data.answers.forEach((group, groupIndex) => {
    group.forEach((id, idIndex) => {
      if (!declaredIds.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `answers[${groupIndex}][${idIndex}]: itemId "${id}" is not declared in any ROW`,
          path: ['answers', groupIndex, idIndex],
        });
      }
    });
  });
}

/**
 * Invariant 3 — no itemId appears in more than one answer group.
 *
 * Checked globally across all groups (not just within a single group).
 */
function validateAnswerUniqueness(
  data: z.infer<typeof MatcherBlockSchema>,
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();

  data.answers.forEach((group, groupIndex) => {
    group.forEach((id, idIndex) => {
      if (seen.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `answers[${groupIndex}][${idIndex}]: itemId "${id}" appears in more than one answer group`,
          path: ['answers', groupIndex, idIndex],
        });
      } else {
        seen.add(id);
      }
    });
  });
}

// Factory and type guard
export function createMatcherBlock(
  name: string,
  rows: MatcherRow[],
  answers: string[][],
  colors?: string[],
): MatcherBlock {
  return MatcherBlockSchema.parse({ type: AST_TYPES.MATCHER_BLOCK, name, rows, answers, colors });
}

export function isMatcherBlock(value: unknown): value is MatcherBlock {
  return MatcherBlockSchema.safeParse(value).success;
}
